// src/whatsappChat/components/WaChatLayout.jsx
import React from 'react';
import { Navigate, Outlet, useOutletContext } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useWaPermissions, useWaUnreadChats } from '../api/hooks';
import { normalizeError } from '../api/client';
import { WaChatNav, WA_CHAT_PATH } from './WaChatNav';
import { WaSocketProvider, useWaSocket } from '../socket/WaSocketProvider';
import '../styles/whatsappChat.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: true, retry: 1 },
    // BotSense cannot de-duplicate: a failed send must never be retried automatically.
    mutations: { retry: 0 },
  },
});

function WaChatShell() {
  const { permissions, isLoading, isError, error, refetch } = useWaPermissions();

  let body;
  if (isLoading) {
    body = <div className="wa-state">Loading…</div>;
  } else if (isError) {
    body = (
      <div className="wa-state">
        <p>{normalizeError(error).message}</p>
        <button type="button" className="wa-btn wa-btn-secondary" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  } else if (!permissions.menu) {
    body = <div className="wa-state">You do not have access to WhatsApp Chat.</div>;
  } else {
    body = <Outlet context={{ permissions }} />;
  }

  return (
    <WaSocketProvider enabled={permissions.menu && permissions.inbox.view}>
      <WaChatFrame permissions={permissions}>{body}</WaChatFrame>
    </WaSocketProvider>
  );
}

function WaChatFrame({ permissions, children }) {
  const inbox = permissions.menu && permissions.inbox.view;
  const { connected } = useWaSocket();
  const unread = useWaUnreadChats(inbox);

  return (
    <div className="wa-chat-container">
      <WaChatNav
        permissions={permissions}
        unreadCount={unread.count}
        unreadMore={unread.more}
        live={inbox ? connected : null}
      />
      <main className="wa-chat-body">{children}</main>
    </div>
  );
}

export default function WaChatLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <WaChatShell />
    </QueryClientProvider>
  );
}

export const useWaChatContext = () => useOutletContext();

/**
 * Page-level gate driven by GET /permissions, e.g. <RequireWaRight right="templates.view">.
 * Sends the user to the first section they may open instead.
 */
export function RequireWaRight({ right, children }) {
  const { permissions } = useWaChatContext();
  const [section, action] = right.split('.');
  if (permissions?.[section]?.[action]) return children;

  const paths = {
    inbox: WA_CHAT_PATH,
    templates: `${WA_CHAT_PATH}/templates`,
    setup: `${WA_CHAT_PATH}/setup`,
  };
  const fallback = Object.keys(paths).find(
    (key) => (key !== section || action !== 'view') && permissions?.[key]?.view
  );

  if (!fallback) {
    return <div className="wa-state">You do not have access to this section.</div>;
  }
  return <Navigate to={paths[fallback]} replace />;
}
